import React, { useState } from "react";
import { api } from "../../lib/api";
import { stepUpHeaders } from "../../gateway/api";
import { AFFILIATE_PATHS } from "../../affiliate/contract";
import { formatMoneyCode } from "../../lib/formatMoney";
import StepUpConfirmDialog from "../../gateway/components/StepUpConfirmDialog";
import { affiliateErrorText } from "../../admin/affiliate/errorText";
import { apiErrorCode } from "../../lib/apiErrors";

/**
 * ★ `POST /super-admin/affiliate/commissions/{id}/reverse` — take an affiliate's money back.
 *
 * WRAPS THE GATEWAY'S DIALOG, and there is no affiliate copy of it (contract §4): the
 * reason textarea, the acknowledgement, the embedded identity check and the
 * `REAUTH_REQUIRED` retry are one component because they are one procedure. `gatewayApi` is
 * threaded through because `StepUpAuth` mints the token from the gateway's own endpoint.
 *
 * IT REVERSES THE FULL UNREVERSED REMAINDER and says so before asking. There is no
 * partial-amount admin reversal: an operator choosing an arbitrary number is an operator
 * with no rule to be audited against. The reason is mandatory — taking money back from a
 * practice without a recorded why is the v0 shape this replaces.
 *
 * THE REFUSAL IS SHOWN IN THE DIALOG'S BODY, not left to its inline slot. That slot renders
 * `gatewayErrorText`, whose closed set is the GATEWAY's, so an affiliate code
 * (`AFFILIATE_COMMISSION_ALREADY_REVERSED` — the very refusal a second click produces, and
 * the thing that makes this endpoint safe to press twice) would read there as "an
 * unexpected error". The specific sentence is rendered above it instead; `code: null` is
 * returned so the operator is never told their identity check failed when it did not.
 */
export default function ReverseCommissionDialog({ commission, open, onClose, gatewayApi, t, tv }) {
  const [refusal, setRefusal] = useState("");

  if (!commission) return null;

  const remainder = Math.max(0, (commission.amountCents || 0) - (commission.reversedCents || 0));

  const confirm = async ({ reason, stepUpToken }) => {
    setRefusal("");
    try {
      await api.post(
        AFFILIATE_PATHS.adminCommissionReverse(commission.id),
        { reason },
        stepUpHeaders(stepUpToken),
      );
      return { ok: true };
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "REAUTH_REQUIRED") return { ok: false, code };
      setRefusal(affiliateErrorText(e, { t, tv, prefix: "saff", fallback: tv("errors.saveFailed") }));
      return { ok: false, code: null };
    }
  };

  return (
    <StepUpConfirmDialog
      open={open}
      onClose={(didMutate) => { setRefusal(""); onClose(didMutate); }}
      titleKey="saff.reverse.title"
      descKey="saff.reverse.desc"
      requireAck
      ackKey="saff.reverse.ack"
      requireReason
      reasonMinLen={5}
      reasonPlaceholderKey="saff.reverse.reasonPlaceholder"
      onConfirm={confirm}
      gatewayApi={gatewayApi}
    >
      <div className="text-sm text-slate-600 space-y-1" data-testid="reverse-summary">
        <div>
          {t("saff.reverse.remainder", {
            amount: formatMoneyCode(remainder, commission.currency),
            affiliate: commission.affiliateName || commission.affiliateTenantId,
          })}
        </div>
        {refusal && (
          <p className="text-xs text-rose-600" role="alert" data-testid="reverse-refusal">{refusal}</p>
        )}
      </div>
    </StepUpConfirmDialog>
  );
}
